/**
 * URL Injection Utilities
 *
 * Functions for injecting locale, country, and user identifiers into URLs.
 * Used by Iframe component and anywhere else that needs parameterized URLs.
 *
 * Placeholder formats:
 * - %%COUNTRY%% - Country slug (e.g., 'us', 'gb', 'de')
 * - %%LANGUAGE%% - Language slug (e.g., 'en', 'fr', 'de')
 * - %%SFID%% - SalesForce ID (or other CRM ID)
 */

/**
 * URL placeholder constants
 * These must match what's used in Contentful URL fields
 */
export const URL_PLACEHOLDERS = {
  COUNTRY: '%%COUNTRY%%',
  LANGUAGE: '%%LANGUAGE%%',
  /** SalesForce ID - used by both Breitling and Gallet */
  SFID: '%%SFID%%',
  /** Generic user ID placeholder (alias for SFID) */
  USER_ID: '%%USERID%%',
} as const;

/**
 * Options for URL injection
 */
export interface UrlInjectionOptions {
  /** Country slug (e.g., 'us', 'gb') */
  countrySlug?: string;
  /** Language slug (e.g., 'en', 'fr') */
  languageSlug?: string;
  /** User's CRM ID (e.g., SalesForce ID) */
  crmId?: string;
  /** Custom placeholders to inject */
  customPlaceholders?: Record<string, string>;
}

/**
 * Inject locale and user identifiers into a URL
 *
 * @param url - URL with placeholders
 * @param options - Injection options
 * @returns URL with placeholders replaced
 *
 * @example
 * injectUrlPlaceholders(
 *   'https://forms.breitling.com/warranty?country=%%COUNTRY%%&lang=%%LANGUAGE%%&sfid=%%SFID%%',
 *   { countrySlug: 'us', languageSlug: 'en', crmId: 'SF123456' }
 * )
 * // Returns: 'https://forms.breitling.com/warranty?country=us&lang=en&sfid=SF123456'
 */
export function injectUrlPlaceholders(url: string, options: UrlInjectionOptions = {}): string {
  let result = url;

  const { countrySlug, languageSlug, crmId, customPlaceholders } = options;

  // Inject country
  if (countrySlug !== undefined) {
    result = result.replaceAll(URL_PLACEHOLDERS.COUNTRY, countrySlug);
  }

  // Inject language
  if (languageSlug !== undefined) {
    result = result.replaceAll(URL_PLACEHOLDERS.LANGUAGE, languageSlug);
  }

  // Inject CRM ID (both SFID and USER_ID placeholders)
  if (crmId !== undefined) {
    result = result.replaceAll(URL_PLACEHOLDERS.SFID, crmId);
    result = result.replaceAll(URL_PLACEHOLDERS.USER_ID, crmId);
  } else {
    // Remove unfilled CRM placeholders (don't leave %%SFID%% in URL)
    result = result.replaceAll(URL_PLACEHOLDERS.SFID, '');
    result = result.replaceAll(URL_PLACEHOLDERS.USER_ID, '');
  }

  // Inject custom placeholders
  if (customPlaceholders) {
    for (const [placeholder, value] of Object.entries(customPlaceholders)) {
      result = result.replaceAll(placeholder, value);
    }
  }

  return result;
}

/**
 * Check if a URL contains any known placeholders
 * Used to determine if we need to wait for user data before rendering
 *
 * @param url - URL to check
 * @returns true if URL contains placeholders
 */
export function hasUrlPlaceholders(url: string): boolean {
  return Object.values(URL_PLACEHOLDERS).some((placeholder) => url.includes(placeholder));
}

/**
 * Check if a URL requires user authentication (contains user ID placeholder)
 *
 * @param url - URL to check
 * @returns true if URL needs user ID
 */
export function requiresUserAuth(url: string): boolean {
  return url.includes(URL_PLACEHOLDERS.SFID) || url.includes(URL_PLACEHOLDERS.USER_ID);
}

/**
 * Parse locale from router path (e.g., 'us-en') into country and language slugs
 *
 * @param locale - Router locale string (e.g., 'us-en', 'gb-en', 'de-de')
 * @returns Object with countrySlug and languageSlug
 */
export function parseLocaleForUrl(locale: string): {
  countrySlug: string;
  languageSlug: string;
} {
  const [countrySlug = 'us', languageSlug = 'en'] = locale.split('-');
  return { countrySlug, languageSlug };
}

/**
 * Merge query parameters from current route into an iframe URL
 * Useful for passing through UTM parameters, etc.
 *
 * @param iframeUrl - Base iframe URL
 * @param routeParams - Query params from current route (e.g., from useSearchParams)
 * @param excludeParams - Params to exclude from merging (e.g., 'slug')
 * @returns URL with merged query params
 */
export function mergeRouteParamsIntoUrl(
  iframeUrl: string,
  routeParams: URLSearchParams | Record<string, string>,
  excludeParams: string[] = ['slug']
): string {
  try {
    const url = new URL(iframeUrl);
    const params =
      routeParams instanceof URLSearchParams ? routeParams : new URLSearchParams(routeParams);

    params.forEach((value, key) => {
      if (!excludeParams.includes(key)) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  } catch {
    // Invalid URL, return as-is
    return iframeUrl;
  }
}
